/**
 * @fileoverview Dialog de Termos de Uso e Política de Privacidade (LGPD).
 *
 * Exibe conteúdo jurídico completo em português incluindo:
 * - Termos de Uso: aceitação, planos, uso aceitável, responsabilidades
 * - Política de Privacidade: dados coletados, direitos LGPD, segurança, retenção
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Shield, FileText, Lock, Eye, Trash2, AlertTriangle } from 'lucide-react';

// Single source of truth for the "última atualização" stamp — bump when the
// content below is materially changed so the marketing/legal team can track
// when users were last shown a new version.
const LAST_UPDATED = '19 de março de 2026';
const DPO_EMAIL = 'privacidade@blacklegacy.com.br';

interface TermsDialogProps {
  open: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy';
}

export function TermsDialog({ open, onClose, type }: TermsDialogProps) {
  const isTerms = type === 'terms';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center gap-2 mb-1">
            {isTerms
              ? <FileText className="h-5 w-5 text-secondary" aria-hidden="true" />
              : <Shield className="h-5 w-5 text-secondary" aria-hidden="true" />}
            <DialogTitle className="text-lg">
              {isTerms ? 'Termos de Uso' : 'Política de Privacidade'}
            </DialogTitle>
          </div>
          <DialogDescription>
            Última atualização: {LAST_UPDATED}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="px-6 pb-6 max-h-[60vh]">
          {isTerms ? <TermsContent /> : <PrivacyContent />}
        </ScrollArea>

        <div className="p-4 border-t">
          <Button onClick={onClose} className="w-full">Entendi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TermsContent() {
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-sm text-muted-foreground">
      <h3 className="text-foreground font-bold">1. Aceitação dos Termos</h3>
      <p>Ao acessar e utilizar a plataforma Legacy CRM ("Plataforma"), operada pela Black Legacy Tecnologia LTDA ("Empresa"), você concorda com estes Termos de Uso. Se não concordar, interrompa o uso imediatamente.</p>

      <h3 className="text-foreground font-bold">2. Descrição do Serviço</h3>
      <p>A Plataforma oferece serviços de CRM, gestão de tarefas, funil de vendas, automações e inteligência artificial para agências e empresas. Os serviços são disponibilizados via modelo SaaS (Software como Serviço).</p>

      <h3 className="text-foreground font-bold">3. Cadastro e Conta</h3>
      <p>Você é responsável pela veracidade dos dados fornecidos no cadastro. A Empresa reserva-se o direito de suspender contas com informações falsas ou atividades fraudulentas.</p>

      <h3 className="text-foreground font-bold">4. Planos e Pagamento</h3>
      <p>Os planos pagos são cobrados via cartão de crédito ou boleto através do Stripe. O cancelamento pode ser feito a qualquer momento, com acesso mantido até o final do período pago. Não há reembolso proporcional.</p>

      <h3 className="text-foreground font-bold">5. Uso Aceitável</h3>
      <p>É proibido utilizar a Plataforma para: atividades ilegais, envio de spam, violação de propriedade intelectual, tentativas de acesso não autorizado ou qualquer ação que prejudique outros usuários.</p>

      <h3 className="text-foreground font-bold">6. Propriedade Intelectual</h3>
      <p>Todo o código, design, marcas e conteúdo da Plataforma são propriedade da Empresa. Os dados inseridos pelo usuário permanecem de propriedade do usuário.</p>

      <h3 className="text-foreground font-bold">7. Limitação de Responsabilidade</h3>
      <p>A Plataforma é fornecida "como está". A Empresa não se responsabiliza por perdas de dados, indisponibilidade temporária ou danos indiretos decorrentes do uso.</p>

      <h3 className="text-foreground font-bold">8. Rescisão</h3>
      <p>A Empresa pode suspender ou encerrar contas que violem estes Termos, com notificação prévia quando possível. O usuário pode encerrar sua conta a qualquer momento nas configurações.</p>

      <h3 className="text-foreground font-bold">9. Foro</h3>
      <p>Fica eleito o foro da comarca de São Paulo/SP para dirimir quaisquer questões decorrentes destes Termos.</p>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-sm text-muted-foreground">
      <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/10 text-secondary text-xs font-medium mb-4">
        <Shield className="h-4 w-4 shrink-0" aria-hidden="true" />
        Em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018)
      </div>

      <h3 className="text-foreground font-bold flex items-center gap-2"><Eye className="h-4 w-4" aria-hidden="true" /> 1. Dados Coletados</h3>
      <p>Coletamos os seguintes dados pessoais:</p>
      <ul className="list-disc pl-4 space-y-1">
        <li><strong>Dados de cadastro:</strong> nome, email, senha (criptografada)</li>
        <li><strong>Dados de uso:</strong> páginas visitadas, features utilizadas, tempo de sessão</li>
        <li><strong>Dados de pagamento:</strong> processados exclusivamente pelo Stripe (não armazenamos dados de cartão)</li>
        <li><strong>Dados inseridos:</strong> leads, tarefas, clientes e documentos criados na plataforma</li>
      </ul>

      <h3 className="text-foreground font-bold flex items-center gap-2"><Lock className="h-4 w-4" aria-hidden="true" /> 2. Finalidade do Tratamento</h3>
      <p>Seus dados são utilizados para: prestação do serviço contratado, melhorias na plataforma, comunicações sobre conta e funcionalidades, e cumprimento de obrigações legais.</p>

      <h3 className="text-foreground font-bold flex items-center gap-2"><Shield className="h-4 w-4" aria-hidden="true" /> 3. Segurança</h3>
      <p>Utilizamos criptografia TLS/SSL, Row-Level Security (RLS) para isolamento de dados entre workspaces, autenticação via Supabase Auth com suporte a MFA, backups automáticos e monitoramento contínuo.</p>

      <h3 className="text-foreground font-bold flex items-center gap-2"><Trash2 className="h-4 w-4" aria-hidden="true" /> 4. Direitos do Titular (LGPD Art. 18)</h3>
      <p>Você tem direito a:</p>
      <ul className="list-disc pl-4 space-y-1">
        <li><strong>Acesso:</strong> solicitar cópia dos seus dados pessoais</li>
        <li><strong>Correção:</strong> corrigir dados incompletos ou desatualizados</li>
        <li><strong>Exclusão:</strong> solicitar a eliminação dos seus dados (exceto quando a lei exigir retenção)</li>
        <li><strong>Portabilidade:</strong> exportar seus dados em formato CSV</li>
        <li><strong>Revogação:</strong> revogar o consentimento a qualquer momento</li>
      </ul>

      <h3 className="text-foreground font-bold">5. Compartilhamento</h3>
      <p>Não vendemos seus dados. Compartilhamos apenas com: Stripe (pagamentos), Supabase (infraestrutura), e provedores de AI (apenas prompts de trabalho, sem dados pessoais).</p>

      <h3 className="text-foreground font-bold">6. Retenção</h3>
      <p>Dados da conta são retidos enquanto a conta estiver ativa. Após exclusão, os dados são removidos em até 30 dias, exceto quando exigido por lei (até 5 anos para dados financeiros).</p>

      <h3 className="text-foreground font-bold flex items-center gap-2"><AlertTriangle className="h-4 w-4" aria-hidden="true" /> 7. Incidentes</h3>
      <p>Em caso de incidente de segurança, notificaremos a ANPD e os titulares afetados em até 72 horas, conforme Art. 48 da LGPD.</p>

      <h3 className="text-foreground font-bold">8. Contato do DPO</h3>
      <p>
        Para exercer seus direitos ou tirar dúvidas sobre privacidade:{' '}
        <a
          href={`mailto:${DPO_EMAIL}`}
          className="text-secondary font-semibold hover:underline"
        >
          {DPO_EMAIL}
        </a>
      </p>
    </div>
  );
}
